import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "motion/react";
import {
  forwardRef,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Link, type LinkProps } from "react-router-dom";
import { Spinner } from "./Spinner";
import { useSurface, type SurfaceKind } from "./Surface";

const DISABLED_SOLID =
  "disabled:bg-gray-scale-500 disabled:text-on-media aria-disabled:bg-gray-scale-500 aria-disabled:text-on-media";
const DISABLED_FADE = "disabled:opacity-50 aria-disabled:opacity-50";
const PRESS = "motion-safe:active:scale-[0.98]";
const HOVER_DANGER = "hover:text-primary-red hover:opacity-100";

/**
 * The single clickable surface: `variant` treatment, `tone` palette color, `size` alone decides height,
 * `weight` the label weight, `elevation` the shadow of floating actions, `hoverTone` a hover-only colour (a neutral
 * destructive link) and `aspect="square"` an icon-only box at a text size's height. Disabled looks live here only.
 * `variant="text"` is a bare coloured word (no underline, no hover change), e.g. "Copiar" next to a code.
 * `variant="dashed-soft"` is the quiet 1px dashed chip at 60% of the tone on no fill ("Adicionar observação").
 * `tone="game"` is the prize games' fixed palette: `solid` ink with ivory text, `plain` the quiet muted-ink link, `link`/`text` gold.
 */
export const buttonVariants = cva(
  [
    "inline-flex items-center gap-2 leading-none select-none",
    "transition-[color,background-color,border-color,box-shadow,transform] duration-base ease-standard",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
    "disabled:pointer-events-none aria-disabled:pointer-events-none",
  ],
  {
    variants: {
      variant: {
        solid: PRESS,
        outline: cn("border bg-card", PRESS),
        soft: PRESS,
        surface: "border border-border bg-card text-card-text motion-safe:active:scale-[0.99]",
        ghost: "bg-transparent motion-safe:active:scale-95",
        plain: "bg-transparent motion-safe:active:scale-95",
        link: "h-auto p-0 bg-transparent underline underline-offset-4 hover:opacity-80",
        text: "h-auto p-0 bg-transparent",
        dashed: cn("border-2 border-dashed bg-transparent", PRESS, DISABLED_FADE),
        "dashed-soft": cn("border border-dashed bg-transparent", PRESS, DISABLED_FADE),
        tab: "border-b-2 border-transparent bg-transparent",
        chip: "bg-card shadow-raised motion-safe:hover:scale-110 motion-safe:active:scale-95",
        elevated: "bg-card shadow-raised hover:bg-page",
        glass: cn("bg-scrim/60 text-on-media backdrop-blur-sm hover:bg-scrim/80 motion-safe:active:scale-95", DISABLED_FADE),
      },
      tone: {
        primary: "",
        secondary: "",
        danger: "",
        neutral: "",
        game: "",
        night: "",
      },
      size: {
        xs: "h-8 px-3 text-xs",
        sm: "h-10 px-4 text-sm",
        md: "h-12 px-5 text-base",
        lg: "h-14 px-6 text-lg",
        footer: "h-14 px-3 text-lg md:px-6",
        row: "min-h-12 px-4 py-3 text-base",
        "icon-xs": "h-7 w-7 shrink-0 p-0",
        "icon-sm": "h-9 w-9 shrink-0 p-0",
        "icon-bar": "h-9 w-9 shrink-0 p-0",
        "icon-md": "h-11 w-11 shrink-0 p-0",
        "icon-lg": "h-12 w-12 shrink-0 p-0",
        tab: "h-auto px-2 pb-1.5 pt-2",
        auto: "h-auto",
        none: "",
      },
      aspect: {
        auto: "",
        square: "aspect-square px-0",
      },
      shape: {
        rounded: "rounded-control",
        pill: "rounded-full",
        circle: "rounded-full",
        square: "rounded-none",
      },
      align: {
        center: "justify-center",
        between: "justify-between",
        start: "justify-start text-left",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
      selected: {
        true: "",
        false: "",
      },
      weight: {
        normal: "font-normal",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
      },
      elevation: {
        none: "",
        raised: "shadow-raised hover:shadow-float",
        float: "shadow-float",
      },
      hoverTone: {
        none: "",
        danger: "",
      },
    },

    compoundVariants: [
      {
        variant: "solid",
        tone: "primary",
        class: cn("bg-primary text-primary-foreground hover:brightness-95", DISABLED_SOLID),
      },
      {
        variant: "solid",
        tone: "secondary",
        class: cn("bg-secondary text-secondary-foreground hover:brightness-95", DISABLED_SOLID),
      },
      {
        variant: "solid",
        tone: "danger",
        class: cn("bg-primary-red text-primary-red-foreground hover:brightness-95", DISABLED_SOLID),
      },
      {
        variant: "solid",
        tone: "neutral",
        class: cn("bg-card-text/45 text-on-media hover:brightness-95", DISABLED_SOLID),
      },

      {
        variant: "outline",
        tone: "primary",
        class: "border-primary text-primary hover:bg-primary/10 disabled:opacity-50",
      },
      {
        variant: "outline",
        tone: "secondary",
        class: "border-secondary text-secondary hover:bg-secondary/10 disabled:opacity-50",
      },
      {
        variant: "outline",
        tone: "danger",
        class: "border-primary-red text-primary-red hover:bg-primary-red/10 disabled:opacity-50",
      },
      {
        variant: "outline",
        tone: "neutral",
        class: "border-border text-card-text/75 hover:bg-card-text/5 disabled:opacity-50",
      },

      {
        variant: "soft",
        tone: "primary",
        class: "bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50",
      },
      {
        variant: "soft",
        tone: "secondary",
        class: "bg-secondary/10 text-secondary hover:bg-secondary/20 disabled:opacity-50",
      },
      {
        variant: "soft",
        tone: "danger",
        class: "bg-primary-red/10 text-primary-red hover:bg-primary-red/20 disabled:opacity-50",
      },
      {
        variant: "soft",
        tone: "neutral",
        class: "bg-card-text/5 text-card-text/75 hover:bg-card-text/10 disabled:opacity-50",
      },

      { variant: "surface", tone: "neutral", class: "hover:bg-card-text/5" },
      { variant: "surface", tone: "primary", class: "hover:border-primary/60" },
      { variant: "surface", tone: "secondary", class: "hover:border-secondary/60" },
      { variant: "surface", tone: "danger", class: "hover:border-primary-red/60" },

      {
        variant: ["ghost", "plain"],
        class: "disabled:text-card-text/30 disabled:opacity-100",
      },
      { variant: "ghost", tone: "primary", class: "text-primary hover:bg-primary/10" },
      { variant: "ghost", tone: "secondary", class: "text-secondary hover:bg-secondary/10" },
      { variant: "ghost", tone: "danger", class: "text-primary-red hover:bg-primary-red/10" },
      { variant: "ghost", tone: "neutral", class: "text-card-text/75 hover:bg-card-text/5" },

      { variant: "chip", tone: "primary", class: "text-primary" },
      { variant: "chip", tone: "secondary", class: "text-secondary" },
      { variant: "chip", tone: "danger", class: "text-primary-red" },
      { variant: "chip", tone: "neutral", class: "text-card-text" },

      { variant: "elevated", tone: "primary", class: "text-primary" },
      { variant: "elevated", tone: "secondary", class: "text-secondary" },
      { variant: "elevated", tone: "danger", class: "text-primary-red" },
      { variant: "elevated", tone: "neutral", class: "text-card-text" },

      { variant: "plain", tone: "primary", class: "text-primary" },
      { variant: "plain", tone: "secondary", class: "text-secondary" },
      { variant: "plain", tone: "danger", class: "text-primary-red" },
      { variant: "plain", tone: "neutral", class: "text-card-text/75 hover:text-card-text" },

      { variant: "link", tone: "primary", class: "text-primary" },
      { variant: "link", tone: "secondary", class: "text-secondary" },
      { variant: "link", tone: "danger", class: "text-primary-red" },
      { variant: "link", tone: "neutral", class: "text-card-text/75" },

      { variant: "text", tone: "primary", class: "text-primary" },
      { variant: "text", tone: "secondary", class: "text-secondary" },
      { variant: "text", tone: "danger", class: "text-primary-red" },
      { variant: "text", tone: "neutral", class: "text-card-text/75" },
      { variant: "text", tone: "game", class: "text-game-gold-deep focus-visible:ring-game-gold" },

      { variant: "soft", tone: "primary", selected: true, class: "bg-primary/20 text-primary" },
      { variant: "outline", tone: "primary", selected: true, class: "bg-primary/10 text-primary" },
      { variant: "surface", selected: true, class: "border-primary bg-primary/10 text-primary" },
      {
        variant: "surface",
        tone: "secondary",
        selected: true,
        class: "border-secondary bg-secondary/10 text-secondary",
      },
      { variant: "ghost", selected: true, class: "bg-card-text/5" },

      { variant: "dashed", tone: "primary", class: "border-primary bg-primary/5 text-primary hover:bg-primary/10" },
      { variant: "dashed", tone: "secondary", class: "border-secondary bg-secondary/5 text-secondary hover:bg-secondary/10" },
      { variant: "dashed", tone: "danger", class: "border-primary-red bg-primary-red/5 text-primary-red hover:bg-primary-red/10" },
      { variant: "dashed", tone: "neutral", class: "border-border text-card-text/75 hover:bg-card-text/5" },

      { variant: "dashed-soft", tone: "primary", class: "border-primary/60 text-primary hover:bg-primary/5" },
      { variant: "dashed-soft", tone: "secondary", class: "border-secondary/60 text-secondary hover:bg-secondary/5" },
      { variant: "dashed-soft", tone: "danger", class: "border-primary-red/60 text-primary-red hover:bg-primary-red/5" },
      { variant: "dashed-soft", tone: "neutral", class: "border-border text-card-text/75 hover:bg-card-text/5" },

      { variant: "tab", class: "rounded-none" },
      { variant: "tab", tone: "primary", selected: true, class: "border-primary text-primary" },
      { variant: "tab", tone: "secondary", selected: true, class: "border-secondary text-secondary" },
      { variant: "tab", tone: "danger", selected: true, class: "border-primary-red text-primary-red" },

      {
        variant: "solid",
        tone: "game",
        class:
          "bg-game-ink text-game-ivory shadow-game-button hover:bg-game-ink-light focus-visible:ring-game-gold motion-safe:active:scale-[0.99] disabled:opacity-70 aria-disabled:opacity-70",
      },
      { variant: "link", tone: "game", class: "text-game-gold-deep focus-visible:ring-game-gold" },
      {
        variant: "plain",
        tone: "game",
        class: "text-game-muted underline-offset-4 hover:text-game-ink hover:underline focus-visible:ring-game-gold",
      },

      {
        variant: "solid",
        tone: "night",
        class: cn("bg-primary text-primary-foreground shadow-night-glow hover:brightness-110", DISABLED_SOLID),
      },
      {
        variant: "outline",
        tone: "night",
        class: "border-night-ink/50 bg-night-sky/60 text-night-ink backdrop-blur-sm hover:bg-night-ink/10 disabled:opacity-50",
      },

      { hoverTone: "danger", class: HOVER_DANGER },
    ],

    defaultVariants: {
      variant: "solid",
      tone: "primary",
      size: "lg",
      shape: "rounded",
      align: "center",
      fullWidth: false,
      selected: false,
      weight: "bold",
      elevation: "none",
      hoverTone: "none",
      aspect: "auto",
    },
  },
);

type ButtonStyleProps = VariantProps<typeof buttonVariants>;

type ButtonTone = NonNullable<ButtonStyleProps["tone"]>;
type ButtonVariant = NonNullable<ButtonStyleProps["variant"]>;
/** Variants that carry `tone="game"` styles; any other pairing would render unstyled, so the type rejects it. */
export type GameButtonVariant = Extract<ButtonVariant, "solid" | "link" | "plain" | "text" | "tab">;
/** Variants that carry `tone="night"` (seasonal night scene) styles: a glowing solid and a moonlit glass outline. */
export type NightButtonVariant = Extract<ButtonVariant, "solid" | "outline">;
type ButtonToneProps =
  | { tone?: Exclude<ButtonTone, "game" | "night"> | null; variant?: ButtonStyleProps["variant"] }
  | { tone: "game"; variant?: GameButtonVariant | null }
  | { tone: "night"; variant?: NightButtonVariant | null };

type SharedProps = Omit<ButtonStyleProps, "tone" | "variant"> & ButtonToneProps & {
  className?: string;
  /** Disables the control and prefixes a spinner that inherits its text color (`tone="game"`: the 16px gold game ring). */
  isLoading?: boolean;
  children?: ReactNode;
};

const TAB_TONE: Record<SurfaceKind, { idle: string; neutral: string }> = {
  card: {
    idle: "text-card-text/75 hover:text-card-text disabled:text-card-text/30",
    neutral: "border-card-text text-card-text",
  },
  page: {
    idle: "text-page-text/70 hover:text-page-text disabled:text-page-text/30",
    neutral: "border-page-text text-page-text",
  },
  game: {
    idle: "text-game-muted hover:text-game-ink disabled:text-game-ink/30",
    neutral: "border-game-ink text-game-ink",
  },
  night: {
    idle: "text-night-ink/75 hover:text-night-ink disabled:text-night-ink/30",
    neutral: "border-night-ink text-night-ink",
  },
};

function tabToneClass(props: SharedProps, surface: SurfaceKind): string | undefined {
  if (props.variant !== "tab") return undefined;
  if (!props.selected) return TAB_TONE[surface].idle;
  return props.tone === "neutral" ? TAB_TONE[surface].neutral : undefined;
}

function splitSurface<T extends SharedProps>(props: T, surface: SurfaceKind) {
  const {
    variant,
    tone,
    size,
    shape,
    align,
    fullWidth,
    selected,
    weight,
    elevation,
    hoverTone,
    aspect,
    className,
    isLoading = false,
    children,
    ...rest
  } = props;
  return {
    rest,
    isLoading,
    selectedAttr: selected || undefined,
    currentAttr: variant === "tab" && selected ? ("true" as const) : undefined,
    className: cn(
      buttonVariants({ variant, tone, size, shape, align, fullWidth, selected, weight, elevation, hoverTone, aspect }),
      tabToneClass(props, surface),
      hoverTone === "danger" && HOVER_DANGER,
      className,
    ),
    content: (
      <>
        {isLoading && (tone === "game" ? <Spinner size="xs" tone="game" /> : <Spinner tone="current" />)}
        {children}
      </>
    ),
  };
}

export type ButtonProps = SharedProps & Omit<ComponentPropsWithoutRef<"button">, "className" | "children">;

/** Native `<button>` with the shared surface; defaults to `type="button"`; a selected `tab` gets `aria-current`. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const { rest, isLoading, selectedAttr, currentAttr, className, content } = splitSurface(props, useSurface());
  const { type = "button", disabled, ...native } = rest;
  return (
    <button
      aria-current={currentAttr}
      {...native}
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      data-selected={selectedAttr}
      className={className}
    >
      {content}
    </button>
  );
});

export type ButtonLinkProps = SharedProps & Omit<LinkProps, "className" | "children">;

/** Same surface as Button, rendered as a react-router Link. */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(props, ref) {
  const { rest, selectedAttr, currentAttr, className, content } = splitSurface(props, useSurface());
  return (
    <Link aria-current={currentAttr} {...rest} ref={ref} data-selected={selectedAttr} className={className}>
      {content}
    </Link>
  );
});

export type ButtonAnchorProps = SharedProps & Omit<ComponentPropsWithoutRef<"a">, "className" | "children">;

/** Same surface as Button for an external `<a>`; `target="_blank"` gets `rel="noopener noreferrer"`. */
export const ButtonAnchor = forwardRef<HTMLAnchorElement, ButtonAnchorProps>(function ButtonAnchor(props, ref) {
  const { rest, selectedAttr, className, content } = splitSurface(props, useSurface());
  const { rel, target, ...native } = rest;
  return (
    <a
      {...native}
      ref={ref}
      target={target}
      rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
      data-selected={selectedAttr}
      className={className}
    >
      {content}
    </a>
  );
});

const createMotionButton = () => motion.create(Button);

type MotionButtonComponent = ReturnType<typeof createMotionButton>;

export type MotionButtonProps = ComponentProps<MotionButtonComponent>;

let motionButton: MotionButtonComponent | null = null;

/**
 * Button driven by motion (`animate`, `whileTap`). Created lazily on first render so tests that stub
 * `motion/react` need no complete mock to render a plain Button.
 */
export function MotionButton(props: MotionButtonProps) {
  motionButton ??= createMotionButton();
  const Component = motionButton;
  return <Component {...props} />;
}
